/**
 * MSW cart guards
 *
 * 1) Upsell guard — the $15 extra strap (extra-hook-strap-1) is only allowed in the cart
 *    when a Cap + Strap combo (msw-cap-strap) is present; its quantity is capped to the combo quantity.
 *
 * 2) Free-strap gift — shoppers who arrive with the sign-up code (?discount=FREEMSWSTRAP, or the
 *    discount cookie Shopify sets from that URL) get the Sand Beige Extra Hook Strap added
 *    automatically whenever a combo is in the cart, tagged with the _free_gift line property
 *    (the drawer renders that as FREE). It's removed if the combo goes, re-added if the combo comes back.
 *    At checkout the code is re-applied via /discount/<CODE> so the $0 sticks.
 *
 * window.mswCheckUpsell(opts) -> Promise<boolean>   resolves true if it changed the cart
 *   opts.refresh (default true): re-render the cart drawer after a change
 * window.mswHasFreeStrap() -> boolean
 * window.mswFreeStrapCode  -> 'FREEMSWSTRAP'
 */
(function() {
  'use strict';

  var COMBO_HANDLE  = 'msw-cap-strap';
  var UPSELL_HANDLE = 'extra-hook-strap-1';
  var GIFT_HANDLE   = 'extra-hook-strap';
  var GIFT_COLOR    = 'Sand Beige';
  var GIFT_CODE     = 'FREEMSWSTRAP';
  var LS_KEY        = 'msw_free_strap';
  var inflight = null;
  var giftVariantId = null;

  window.mswFreeStrapCode = GIFT_CODE;

  /* ── code detection ─────────────────────────────────────────────────────────── */
  function detectCode() {
    try {
      var p = new URLSearchParams(window.location.search).get('discount');
      if (p && p.toUpperCase() === GIFT_CODE) { localStorage.setItem(LS_KEY, '1'); return; }
      var m = document.cookie.match(/(?:^|;\s*)discount_code=([^;]+)/);
      if (m && decodeURIComponent(m[1]).toUpperCase() === GIFT_CODE) localStorage.setItem(LS_KEY, '1');
    } catch (e) {}
  }
  function hasCode() { try { return localStorage.getItem(LS_KEY) === '1'; } catch (e) { return false; } }
  window.mswHasFreeStrap = hasCode;

  /* ── gift variant lookup (cached per session) ───────────────────────────────── */
  function getGiftVariantId() {
    if (giftVariantId) return Promise.resolve(giftVariantId);
    try { var c = sessionStorage.getItem('msw_gift_vid'); if (c) { giftVariantId = parseInt(c, 10); return Promise.resolve(giftVariantId); } } catch (e) {}
    return fetch(window.Shopify.routes.root + 'products/' + GIFT_HANDLE + '.js')
      .then(function(r) { return r.json(); })
      .then(function(p) {
        var v = (p.variants || []).find(function(x) { return x.available && String(x.option1).trim() === GIFT_COLOR; })
             || (p.variants || []).find(function(x) { return x.available; });
        if (!v) return null;
        giftVariantId = v.id;
        try { sessionStorage.setItem('msw_gift_vid', String(v.id)); } catch (e) {}
        return v.id;
      })
      .catch(function() { return null; });
  }

  /* ── main check ─────────────────────────────────────────────────────────────── */
  function checkCart(opts) {
    opts = opts || {};
    var shouldRefresh = opts.refresh !== false;
    if (inflight) return inflight;

    inflight = fetch(window.Shopify.routes.root + 'cart.js', { cache: 'no-store' })
      .then(function(res) { return res.json(); })
      .then(function(cart) {
        var items = cart.items || [];
        var comboQty = 0, upsell = [], giftLines = [];
        items.forEach(function(it) {
          if (it.handle === COMBO_HANDLE) comboQty += it.quantity;
          if (it.handle === UPSELL_HANDLE) upsell.push({ key: it.key, qty: it.quantity });
          if (it.handle === GIFT_HANDLE && it.properties && it.properties._free_gift) giftLines.push({ key: it.key, qty: it.quantity });
        });

        var updates = {};
        var changed = false;

        // (1) cap the $15 strap to the combo count, trimming the last lines first
        var upsellQty = upsell.reduce(function(n, u) { return n + u.qty; }, 0);
        if (upsellQty > comboQty) {
          var excess = upsellQty - comboQty;
          for (var k = upsell.length - 1; k >= 0 && excess > 0; k--) {
            if (upsell[k].qty <= excess) { updates[upsell[k].key] = 0; excess -= upsell[k].qty; }
            else { updates[upsell[k].key] = upsell[k].qty - excess; excess = 0; }
          }
        }

        // (2) free strap: exactly one gift line while a combo is present, none otherwise
        var giftQty = giftLines.reduce(function(n, g) { return n + g.qty; }, 0);
        var wantGift = hasCode() && comboQty > 0;
        if (!wantGift && giftQty > 0) {
          giftLines.forEach(function(g) { updates[g.key] = 0; });
        } else if (wantGift && giftQty > 1) {
          giftLines.forEach(function(g, i) { updates[g.key] = i === 0 ? 1 : 0; });
        }

        var doUpdate = Object.keys(updates).length
          ? fetch(window.Shopify.routes.root + 'cart/update.js', {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ updates: updates })
            }).then(function() { changed = true; })
          : Promise.resolve();

        return doUpdate.then(function() {
          if (!(wantGift && giftQty === 0)) return;
          return getGiftVariantId().then(function(vid) {
            if (!vid) return;
            return fetch(window.Shopify.routes.root + 'cart/add.js', {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: [{ id: vid, quantity: 1, properties: { _free_gift: 'true' } }] })
            }).then(function(r) { if (r.ok) changed = true; });
          });
        }).then(function() {
          if (changed && shouldRefresh && typeof window.mswCartRefresh === 'function') window.mswCartRefresh();
          return changed;
        });
      })
      .catch(function(err) { console.error('MSW cart guard error:', err); return false; })
      .finally(function() { inflight = null; });

    return inflight;
  }

  window.mswCheckUpsell = checkCart;

  /* ── cart restore from ?cart=VARIANT:QTY,VARIANT:QTY (abandoned-cart emails) ─────
     Replaces the current cart with those lines (plus the free strap for code holders) and
     opens the drawer. The drawer opens immediately in a loading state; the network work
     (clear → single add.js that also returns the drawer HTML) runs while the page renders. */
  function restoreCartFromParam() {
    var raw;
    try { raw = new URLSearchParams(window.location.search).get('cart'); } catch (e) { return; }
    if (!raw) return;
    var items = raw.split(',').map(function(s) { return s.trim(); }).filter(Boolean).map(function(s) {
      var p = s.split(':'); return { id: parseInt(p[0], 10), quantity: Math.max(1, parseInt(p[1] || '1', 10) || 1) };
    }).filter(function(i) { return i.id > 0; });
    try { var u = new URL(window.location.href); u.searchParams.delete('cart'); window.history.replaceState({}, '', u.toString()); } catch (e) {}
    if (!items.length) return;

    var root = window.Shopify.routes.root;
    var drawerEl = document.querySelector('cart-drawer');

    // 1) open the drawer right away, in a loading state
    if (drawerEl) {
      drawerEl.classList.add('msw-loading');
      requestAnimationFrame(function() { drawerEl.classList.add('animate', 'active'); });
      document.body.classList.add('overflow-hidden', 'msw-drawer-open');
    }

    function applySections(data) {
      var sections = data && data.sections;
      if (!sections) return false;
      var d = sections['cart-drawer'] && new DOMParser().parseFromString(sections['cart-drawer'], 'text/html').querySelector('#CartDrawer');
      var target = document.querySelector('#CartDrawer');
      if (d && target) target.innerHTML = d.innerHTML;
      var b = sections['cart-icon-bubble'] && new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section');
      var bubble = document.getElementById('cart-icon-bubble');
      if (b && bubble) bubble.innerHTML = b.innerHTML;
      return !!d;
    }
    function addItems(list) {
      return fetch(root + 'cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: list, sections: 'cart-drawer,cart-icon-bubble', sections_url: window.location.pathname })
      }).then(function(r) { return r.json(); }).then(function(data) {
        // one unavailable variant fails the whole request — retry the rest one by one
        if (data && data.status && data.status !== 200 && list.length > 1) {
          return list.reduce(function(chain, it) {
            return chain.then(function(last) { return addItems([it]).then(function(d) { return (d && !d.status) ? d : last; }); });
          }, Promise.resolve(null));
        }
        return data;
      });
    }

    // 2) clear + gift lookup in parallel, then one add.js that also returns the drawer HTML
    var giftP = hasCode() ? getGiftVariantId() : Promise.resolve(null);
    Promise.all([fetch(root + 'cart/clear.js', { method: 'POST' }), giftP])
      .then(function(res) {
        var vid = res[1];
        if (vid) items.push({ id: vid, quantity: 1, properties: { _free_gift: 'true' } });
        return addItems(items);
      })
      .then(function(data) {
        var rendered = applySections(data);
        if (drawerEl) drawerEl.classList.remove('is-empty', 'msw-loading');
        if (rendered) {
          if (typeof window.cartUpsellInit === 'function') window.cartUpsellInit();
          if (typeof window.updateFreeShippingBar === 'function') window.updateFreeShippingBar();
          checkCart(); // safety net (e.g. gift without a combo) — re-renders only if it changes something
        } else if (typeof window.mswCartRefresh === 'function') {
          window.mswCartRefresh();
        }
      })
      .catch(function(err) {
        console.error('MSW cart restore error:', err);
        if (drawerEl) drawerEl.classList.remove('msw-loading');
        if (typeof window.mswCartRefresh === 'function') window.mswCartRefresh();
      });
  }

  /* ── product-page banner for code holders ───────────────────────────────────── */
  function showBanner() {
    if (!hasCode()) return;
    document.querySelectorAll('.msw-free-strap-banner').forEach(function(el) { el.style.display = 'flex'; });
  }

  detectCode();
  // ?cart= restore starts immediately (this script runs after the drawer markup, before the page finishes rendering)
  var restoring = false;
  try { restoring = !!new URLSearchParams(window.location.search).get('cart'); } catch (e) {}
  if (restoring) restoreCartFromParam();
  function boot() { showBanner(); if (!restoring) checkCart(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
